import {AsyncInlineError} from '@s-ui/decorators'
import {Repository} from '@s-ui/domain'

import FetcherFactory from '../../fetcher/Fetchers/factory.js'
import {AssistantChatEntity} from '../Entities/AssistantChatEntity.js'
import {NotFoundAssistantError} from '../Errors/NotFoundAssistantError.js'
import {StartNewChatAssistantError} from '../Errors/StartNewChatAssistantError.js'
import {UpdateChatAssistantError} from '../Errors/UpdateChatAssistantError.js'
import {AssistantBlockFiltersMapper} from '../Mappers/AssistantBlockFiltersMapper.js'
import {GetSuggestionsAssistantRequest} from '../Request/GetSuggestionsAssistantRequest.js'
import {SuggestionsAssistantValueObject} from '../ValueObjects/SuggestionsAssistantValueObject.js'

const FETCH_OPTIONS = {timeout: 30000}

export class HTTPAssistantRepository extends Repository {
  _getCustomHeaders({variant}) {
    if (variant) return {'X-Adevinta-MT-Variant': variant}
  }

  #config
  #fetcher
  #assistantBlockFiltersMapper

  static create({config}) {
    return new HTTPAssistantRepository({
      config,
      fetcher: FetcherFactory.httpFetcher({config}),
      assistantBlockFiltersMapper: AssistantBlockFiltersMapper.create({config})
    })
  }

  constructor({config, fetcher, assistantBlockFiltersMapper}) {
    super()
    this.#config = config
    this.#fetcher = fetcher
    this.#assistantBlockFiltersMapper = assistantBlockFiltersMapper
  }

  async checkChat({chatId}) {
    const {NO_CONTENT, NOT_FOUND} = this.#config.get('COMMON').HTTP_STATUS_CODES

    const url = `${this.#config.get('API_URL')}/assistant/chats/${chatId}`
    try {
      const response = await this.#fetcher.get(url, FETCH_OPTIONS)
      if (response.status === NO_CONTENT) return AssistantChatEntity.createDefault({chatId})
    } catch (error) {
      if (error?.response?.status === NOT_FOUND) {
        throw NotFoundAssistantError.create(`[HTTPAssistantRepository#checkChat] Chat ${chatId} not found`)
      }
      throw error
    }
  }

  @AsyncInlineError()
  startNewChat({startAssistantNewChatRequest, variant}) {
    const {text, context} = startAssistantNewChatRequest
    const url = `${this.#config.get('API_URL')}/assistant/chats`
    const customHeaders = this._getCustomHeaders({variant})
    const options = customHeaders ? {...FETCH_OPTIONS, headers: customHeaders} : FETCH_OPTIONS
    return this.#fetcher
      .put(url, {text, context: context.toJSON()}, options)
      .then(({data}) => {
        const blocks = data.response?.blocks?.map?.(this.#assistantBlockFiltersMapper.map) || []
        return AssistantChatEntity.createFromChatApiResponse(data.chatId, {...data.response, blocks})
      })
      .catch(() => {
        throw StartNewChatAssistantError.create('[HTTPAssistantRepository#startNewChat] Error starting new chat')
      })
  }

  @AsyncInlineError()
  updateChat({updateAssistantChatRequest, variant}) {
    const {chatId, text, context} = updateAssistantChatRequest
    const url = `${this.#config.get('API_URL')}/assistant/chats/${chatId}`
    const customHeaders = this._getCustomHeaders({variant})
    const options = customHeaders ? {...FETCH_OPTIONS, headers: customHeaders} : FETCH_OPTIONS
    return this.#fetcher
      .post(url, {text, context: context.toJSON()}, options)
      .then(({data}) => {
        const blocks = data?.blocks?.map?.(this.#assistantBlockFiltersMapper.map) || []
        return AssistantChatEntity.createFromChatApiResponse(chatId, {...data, blocks})
      })
      .catch(() => {
        throw UpdateChatAssistantError.create('[HTTPAssistantRepository#updateChat] Error updating chat')
      })
  }

  @AsyncInlineError()
  async getSuggestions({type, chatId, adId}) {
    const url = `${this.#config.get('API_URL')}/assistant/suggestions`
    const request = GetSuggestionsAssistantRequest.create({type, chatId, adId}, {config: this.#config})

    const {data} = await this.#fetcher.post(url, request.getPayload(), FETCH_OPTIONS)
    return SuggestionsAssistantValueObject.create({suggestions: data.suggestions})
  }
}
